import _ from 'lodash';
import React from 'react';
import Magnifier from 'react-magnifier';

const BOX_COLOR = 'red';
const INSET = 3;

interface Size {
  width: number;
  height: number;
}

interface ImageBoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface PinterestAttachmentObject {
  pin_id: string;
  image_url: string;
  bounding_box?: ImageBoundingBox;
}

interface PinterestAttachmentProps {
  attachmentJson: string;
}

const PinterestAttachment: React.FC<PinterestAttachmentProps> = ({ attachmentJson }) => {
  const [imageSize, setImageSize] = React.useState<Size | null>(null);
  const [containerSize, setContainerSize] = React.useState<Size | null>(null);
  const imageContainerRef = React.useRef<HTMLDivElement | null>(null);

  const attachmentObject: PinterestAttachmentObject = React.useMemo(() => {
    try {
      return JSON.parse(attachmentJson);
    } catch (e) {
      return null;
    }
  }, [attachmentJson]);

  const cacheBustingValue = React.useMemo(() => Math.random(), [attachmentObject]);

  React.useEffect(() => {
    const script = document.createElement('script');

    script.src = '//assets.pinterest.com/js/pinit.js';
    script.async = true;

    document.body.appendChild(script);

    return () => {
      // Had to read minified JS to figure this out :_(
      // HACK: to get the pinterest external widget to reload, we need to remove
      // this cached information from `window`.
      _.keys(window).forEach((key: string) => {
        if (key.startsWith('PIN_')) {
          // @ts-ignore
          delete window[key];
        }
      });

      const parent = script.parentElement;
      if (parent) {
        parent.removeChild(script);
      }
    };
  }, [attachmentObject]);

  React.useEffect(() => {
    const container = imageContainerRef.current;
    const e =
      container &&
      (container.querySelector('#pinterest-image') as HTMLImageElement | undefined | null);
    if (!e) {
      return;
    }
    if (e.naturalWidth && e.naturalHeight) {
      setImageSize({
        width: e!.naturalWidth,
        height: e!.naturalHeight,
      });
    } else {
      e!.onload = () => {
        setImageSize({
          width: e!.naturalWidth,
          height: e!.naturalHeight,
        });
      };
    }
  }, [attachmentObject, imageContainerRef.current]);

  React.useEffect(() => {
    const container = imageContainerRef.current;
    setContainerSize(
      container && {
        width: container.clientWidth,
        height: container.clientHeight,
      },
    );
  }, [imageSize, imageContainerRef.current]);

  if (!attachmentObject) {
    return null;
  }

  const hasBoundingBox =
    !!attachmentObject.bounding_box && _.some(_.values(attachmentObject.bounding_box));
  let x = 0;
  let y = 0;
  let width = 0;
  let height = 0;
  if (!hasBoundingBox && imageSize) {
    width = imageSize.width - 2 * INSET;
    height = imageSize.height - 2 * INSET;
    x = INSET;
    y = INSET;
  } else if (hasBoundingBox) {
    x = attachmentObject.bounding_box!.x;
    y = attachmentObject.bounding_box!.y;
    width = attachmentObject.bounding_box!.width;
    height = attachmentObject.bounding_box!.height;
  }

  let ratio = imageSize && containerSize && containerSize.width / imageSize.width;
  let constrainWidth: number | undefined;
  if (ratio && ratio * imageSize!.height > containerSize!.height) {
    ratio = containerSize!.height / imageSize!.height;
    constrainWidth = ratio * imageSize!.width;
  }

  return (
    <div className="container">
      <div>
        <div className="label-text">
          Image with <span className="box-text">highlighted area</span>:
        </div>
        <div id="image-container" ref={imageContainerRef}>
          <Magnifier
            className="magnifier-wrapper"
            // @ts-ignore
            id="pinterest-image"
            src={`${attachmentObject.image_url}?${cacheBustingValue}`}
          />
          {imageSize && ratio && (
            <svg
              width="100%"
              style={{
                position: 'relative',
                pointerEvents: 'none',
                minHeight: imageSize.height * ratio!,
                minWidth: imageSize.width * ratio!,
                top: -imageSize.height * ratio!,
              }}
            >
              <rect
                width={width * ratio!}
                height={height * ratio!}
                x={x * ratio!}
                y={y * ratio!}
                style={{
                  fill: 'rgba(0,0,0,0)',
                  stroke: BOX_COLOR,
                  strokeWidth: 3,
                }}
              />
            </svg>
          )}
        </div>
      </div>
      <div className="pin-container">
        <div className="label-text">Pin:</div>
        <a
          data-pin-do="embedPin"
          data-pin-width="medium"
          href={`https://www.pinterest.com/pin/${attachmentObject.pin_id}/`}
        ></a>
      </div>
      <style jsx>{`
        .pin-container {
          width: 400px;
        }
        .container {
          visibility: ${imageSize ? 'inherit' : 'hidden'};
          display: flex;
          flex-direction: row;
          justify-content: center;
        }
        #image-container {
          min-width: 400px;
          max-width: 800px;
          margin-right: 40px;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          max-height: calc(100vh - 104px - 140px);
        }
        .box-text {
          color: ${BOX_COLOR};
        }
        .label-text {
          margin-bottom: 8px;
          font-weight: 500;
        }
      `}</style>
      <style jsx global>{`
        .magnifier-wrapper {
          position: relative;
        }
        #pinterest-image {
          position: relative;
          ${constrainWidth && 'min-width: '.concat(constrainWidth.toString()).concat('px;')}
          ${constrainWidth && 'max-width: '.concat(constrainWidth.toString()).concat('px;')}
          border-radius: 5px;
          max-height: calc(100vh - 104px - 140px);
        }
      `}</style>
    </div>
  );
};

export default PinterestAttachment;
